import { TokenCookiesKey } from "/constants"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { actionTypes, makeAction } from "states/actions"
import { checkCanCreateOrder } from "states/ducks/landing"
import { FormHeader } from "styles/index.styles"
import { goTo } from "utils/commonUtils"
import { deleteCookie, setCookie } from "utils/cookies"
import { LandingPageInputs } from "utils/selector"

import { sentryException } from "utils/sentryUtils"
import str from "utils/strings"
import isNotNullOrEmpty from "utils/validate/isNotNullOrEmpty"
import isset from "utils/validate/isset"

import LoadingSpinner from "../modals/loading-spinner"
import {
  ArrowContainer,
  ArrowWhite,
  BtnAndHints,
  CompanyFields,
  ContinueBtn,
  EmailInput,
  Hints,
  NameInput
} from "./landing-fragment.styles"

const LandingPage = (props) => {
  const inputData = useSelector(LandingPageInputs, shallowEqual)
  const { email, nameEN, fromSSO } = inputData
  const canCreateOrder = useSelector(checkCanCreateOrder)

  const dispatch = useDispatch()
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false)

  useEffect(() => {
    dispatch(makeAction(actionTypes.APP_CHECK_LOCATION))
    deleteCookie(TokenCookiesKey)
    return () => {
      formRef.current = null
    }
  }, [])

  const formRef = useRef(null)

  const handleContinue = useCallback(() => {
    import("@utils/webTrack/eventType").then((eventType) => {
      dispatch(makeAction(actionTypes.WEB_TRACK_EVENT, { eventType: eventType.default.clickContinue }))
      formRef.current.submit()
    })
  }, [formRef])

  const handleSubmit = useCallback(() => {
    if (!canCreateOrder) {
      sentryException(new Error("Cannot create order"), {
        email: inputData.email,
        nameEN: inputData.nameEN
      })
      return alert(`${str.TXT_ERROR_Create_Order}`)
    }
    setShowLoadingSpinner(true)
    dispatch(
      makeAction(actionTypes.LANDING_CREATE_ORDER, {
        onSuccessAction: (result) => {
          setShowLoadingSpinner(false)
          if (isNotNullOrEmpty(result)) {
            if (result?.otaToken) {
              setCookie(TokenCookiesKey, result.otaToken)
            }
            const is400Error = isset(result.code) && result.code == 400
            const isEligibleToPurchase = isNotNullOrEmpty(result.check) && result.check.eligibleToPurchase
            if (is400Error || !isEligibleToPurchase) {
              return goTo({
                pathname: `/contactsales`,
                query: { t: Math.random() }
              })
            } else if (!isset(result.code) && isEligibleToPurchase) {
              return goTo({
                pathname: `/detail`,
                query: { t: Math.random() }
              })
            } else {
              sentryException(new Error("Success to create order but response with error code!"))
              return alert(`${str.TXT_ERROR_Create_Order}`)
            }
          } else {
            sentryException(new Error("Success to create order but response with empty order!"))
            return alert(`${str.TXT_ERROR_Create_Order}`)
          }
        },
        onFailureAction: (err) => {
          setShowLoadingSpinner(false)
          if (err) {
            sentryException(err)
          }
          if (isset(err.response) && isset(err.response.status) && err.response.status == 400) {
            return goTo({
              pathname: `/contactsales`,
              query: { t: Math.random() }
            })
          } else {
            return alert(`${str.TXT_ERROR_Create_Order}`)
          }
        }
      })
    )
  }, [setShowLoadingSpinner, canCreateOrder, inputData])

  const emailFieldPath = useMemo(() => ["email"], [])
  const companyNameENFieldPath = useMemo(() => ["nameEN"], [])
  const handleEmailChange = useCallback(({ target }) => {
    dispatch(makeAction(actionTypes.LANDING_SET_EMAIL, { email: target.value }))
  }, [])

  const handleCompanyNameENChange = useCallback(({ target }) => {
    dispatch(makeAction(actionTypes.LANDING_SET_NAME_EN, { nameEN: target.value }))
  }, [])

  return (
    <>
      <FormHeader aria-label={str.TXT_LANDING_Company_Info}>{str.TXT_LANDING_Company_Info}</FormHeader>
      <CompanyFields ref={formRef} forminputdata={inputData} onSubmit={handleSubmit}>
        <EmailInput
          noSpaceError={true}
          value={email}
          fieldPath={emailFieldPath}
          title={str.TXT_LANDING_Fields_Email}
          placeholder={str.TXT_LANDING_PH_Email}
          isRequired={true}
          onChange={handleEmailChange}
          ariaText={str.TXT_ONLINE_FORM_Aria_Label_Company_Email_Input}
        />
        <NameInput
          noSpaceError={true}
          value={nameEN}
          fieldPath={companyNameENFieldPath}
          title={str.TXT_LANDING_Fields_Name}
          placeholder={str.TXT_LANDING_PH_Name}
          isRequired={true}
          onChange={handleCompanyNameENChange}
          ariaText={str.TXT_ONLINE_FORM_Aria_Label_Company_Name_EN_Input}
        />
      </CompanyFields>
      <ContinueBtnAndHints onContinue={handleContinue} />
      {showLoadingSpinner ? <LoadingSpinner /> : null}
    </>
  )
}

const ContinueBtnAndHints = ({ onContinue }) => (
  <BtnAndHints>
    <ContinueBtn onClick={onContinue} aria-label={str.TXT_ONLINE_FORM_Aria_Label_Landing_Continue_Btn}>
      {str.TXT_APP_Continue}
      <RightArrowWhite />
    </ContinueBtn>
    <InputHints />
  </BtnAndHints>
)

const RightArrowWhite = () => (
  <ArrowContainer>
    <ArrowWhite />
  </ArrowContainer>
)

const InputHints = () => (
  <Hints>
    <span style={{ color: "#ff0000" }}>* </span>
    {str.TXT_LANDING_Hints_Required}
  </Hints>
)

export default LandingPage
